import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { type TableColumn } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Dropdown, type DropdownItem } from '../../components/ui/Dropdown';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Modal } from '../../components/ui/Modal';
import { accountManagementService } from '../../services/admin';
import { subscriptionService } from '../../services/subscription';
import type { AdminAccount, AccountListParams, UpdateAccountData } from '../../types/admin';
import type { SubscriptionPlan } from '../../types/subscription';

/**
 * Account Management Page (Admin Only)
 *
 * Provides system administrators with tools to:
 * - View all accounts with pagination and filtering
 * - Search accounts by name or owner
 * - Filter by status and subscription plan
 * - Edit, suspend, reactivate, or delete accounts
 * - View account statistics (members, campaigns)
 */
export const AccountManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination & Filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<'active' | 'suspended' | 'cancelled' | ''>('');

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; accountId: number | null; accountName: string | null }>({
    open: false,
    accountId: null,
    accountName: null,
  });

  // Edit modal state
  const [editModal, setEditModal] = useState<{ open: boolean; account: AdminAccount | null }>({
    open: false,
    account: null,
  });
  const [editFormData, setEditFormData] = useState<UpdateAccountData>({});
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: AccountListParams = {
        page: currentPage,
        per_page: 10,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
      };

      const response = await accountManagementService.getAllAccounts(params);
      setAccounts(response.data);
      setTotalPages(response.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchAccounts();
  }, [currentPage, search, statusFilter]);

  // Handle account actions
  const handleSuspendAccount = async (id: number) => {
    try {
      await accountManagementService.suspendAccount(id);
      setSuccess(t('account_management.messages.account_disabled'));
      fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_management.messages.disable_error'));
    }
  };

  const handleReactivateAccount = async (id: number) => {
    try {
      await accountManagementService.reactivateAccount(id);
      setSuccess(t('account_management.messages.account_enabled'));
      fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_management.messages.enable_error'));
    }
  };

  const handleDeleteAccount = async (id: number, name: string) => {
    setDeleteConfirm({ open: true, accountId: id, accountName: name });
  };

  const confirmDeleteAccount = async () => {
    if (!deleteConfirm.accountId) return;

    try {
      await accountManagementService.deleteAccount(deleteConfirm.accountId);
      setSuccess(t('account_management.messages.account_deleted'));
      setDeleteConfirm({ open: false, accountId: null, accountName: null });
      fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_management.messages.delete_error'));
    }
  };

  // Handle edit account
  const handleEditAccount = async (account: AdminAccount) => {
    setEditModal({ open: true, account });
    setEditFormData({
      name: account.name,
      subscription_plan_id: account.subscription_plan?.id || null,
    });

    // Fetch subscription plans
    setLoadingPlans(true);
    try {
      const response = await subscriptionService.getAvailablePlans();
      setSubscriptionPlans(response.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription plans');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal.account) return;

    try {
      await accountManagementService.updateAccount(editModal.account.id, editFormData);
      setSuccess(t('account_management.messages.account_updated'));
      setEditModal({ open: false, account: null });
      setEditFormData({});
      fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_management.messages.update_error'));
    }
  };

  // Table columns
  const columns: TableColumn<AdminAccount>[] = [
    {
      key: 'id',
      header: t('account_management.table.id'),
    },
    {
      key: 'name',
      header: t('account_management.table.name'),
    },
    {
      key: 'owner',
      header: t('account_management.table.owner'),
      render: (value) => {
        const owner = value as AdminAccount['owner'];
        return `${owner.name} (${owner.email})`;
      },
    },
    {
      key: 'subscription_plan',
      header: t('account_management.table.plan'),
      render: (value) => {
        const plan = value as AdminAccount['subscription_plan'];
        return plan ? plan.name : t('account_management.table.no_plan');
      },
    },
    {
      key: 'status',
      header: t('account_management.table.status'),
      render: (value) => {
        const statusVariant =
          value === 'active' ? 'success' :
          value === 'suspended' ? 'warning' : 'error';
        return (
          <Badge variant={statusVariant}>
            {String(value).toUpperCase()}
          </Badge>
        );
      },
    },
    {
      key: 'members_count',
      header: t('account_management.table.members'),
      render: (value) => value || 0,
    },
    {
      key: 'campaigns_count',
      header: t('account_management.table.campaigns'),
      render: (value) => value || 0,
    },
  ];

  // Actions dropdown for each account
  const renderActions = (account: AdminAccount) => {
    const items: DropdownItem[] = [
      {
        id: 'edit',
        label: t('account_management.actions.edit'),
        onClick: () => handleEditAccount(account),
      },
      {
        id: 'toggle-status',
        label: account.status === 'suspended' ? t('account_management.actions.enable') : t('account_management.actions.disable'),
        onClick: () =>
          account.status === 'suspended'
            ? handleReactivateAccount(account.id)
            : handleSuspendAccount(account.id),
        disabled: account.status === 'cancelled',
      },
    ];

    return (
      <Dropdown
        trigger={
          <Button variant="secondary" size="sm">
            {t('account_management.actions.label')}
          </Button>
        }
        items={items}
        align="right"
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('account_management.page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('account_management.page_subtitle')}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('account_management.search_label')}
            </label>
            <Input
              id="search"
              placeholder={t('account_management.search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('account_management.status_filter_label')}
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'suspended' | 'cancelled' | '');
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('account_management.status_filter_all')}</option>
              <option value="active">{t('account_management.status_active')}</option>
              <option value="suspended">{t('account_management.status_suspended')}</option>
              <option value="cancelled">{t('account_management.status_cancelled')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Accounts Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={accounts}
              renderActions={renderActions}
              emptyMessage={t('account_management.table.empty_message')}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Edit Account Modal */}
      <Modal
        open={editModal.open}
        onClose={() => {
          setEditModal({ open: false, account: null });
          setEditFormData({});
        }}
        title={t('account_management.actions.edit')}
      >
        <div className="space-y-4">
          {/* Account Name */}
          <div>
            <label htmlFor="edit-account-name" className="block text-sm font-medium text-gray-700 mb-1">
              Account Name
            </label>
            <Input
              id="edit-account-name"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              placeholder="Enter account name"
            />
          </div>

          {/* Subscription Plan */}
          <div>
            <label htmlFor="edit-subscription-plan" className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan
            </label>
            {loadingPlans ? (
              <div className="text-sm text-gray-500">Loading plans...</div>
            ) : (
              <select
                id="edit-subscription-plan"
                value={editFormData.subscription_plan_id || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditFormData({
                    ...editFormData,
                    subscription_plan_id: value ? Number(value) : null,
                  });
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No Plan</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price}/{plan.billing_period}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setEditModal({ open: false, account: null });
                setEditFormData({});
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, accountId: null, accountName: null })}
        onConfirm={confirmDeleteAccount}
        title={t('account_management.actions.delete')}
        message={t('account_management.actions.delete_confirm')}
        description={deleteConfirm.accountName ? `Account: ${deleteConfirm.accountName}` : undefined}
        confirmText={t('account_management.actions.delete')}
        variant="danger"
      />
    </div>
  );
};

AccountManagementPage.displayName = 'AccountManagementPage';
